import { Checkbox } from 'antd';
import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import debounce from 'lodash/debounce';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import { ErrorIcon } from 'elem/compPage/my/containers/my-account-profile/UserForm/ErrorIcon';
import styled from './dialog.module.scss';
import gtm from '@/utils/gtm';

export default function TNCModal({
    open = false,
    onClose,
    callback,
    handleRead,
}: {
    open?: boolean;
    onClose?: () => void;
    callback?: () => void;
    handleRead?: (checked: boolean) => void;
}) {
    const container = useRef<HTMLDivElement>(null);
    const readIndicatorRef = useRef<HTMLDivElement>(null);
    const [isScrollRead, setIsScrollRead] = useState(false);
    const [isChecked, setIsChecked] = useState(false);
    const [isErrorShow, setIsErrorShow] = useState(false);

    const onCheckboxChange = (e: CheckboxChangeEvent) => {
        if (!isScrollRead) {
            setIsErrorShow(true);
            return false;
        }
        setIsErrorShow(false);
        setIsChecked(e.target.checked);
        handleRead && handleRead(e.target.checked);
    };

    const handleClose = useCallback(() => {
        setIsScrollRead(false);
        setIsChecked(false);
        handleRead && handleRead(false);
        setIsErrorShow(false);
        onClose && onClose();
    }, [handleRead, onClose]);

    // 移除原有的滚动检测逻辑
    // const debounceFnA = useMemo(
    //     () =>
    //         debounce(() => {
    //             const scrollTop = container?.current?.scrollTop;
    //             if (scrollTop && scrollTop > containerHeight - 300 && !isScrollRead) {
    //                 setIsScrollRead(true);
    //             }
    //         }, 200),
    //     [containerHeight, isScrollRead],
    // );

    const ShowErrorTip = ({ text }: { text: string }) => (
        <div className="my-agreement-checkbox-error-tip ">
            <ErrorIcon />
            {text}
        </div>
    );

    const handleEnsure = () => {
        if (!isScrollRead || !isChecked) {
            setIsErrorShow(true);
            return;
        }
        gtm.push({
            event: 'uaEvent',
            event_name: 'consent_confirm',
            button_name: 'Bestätigen',
            title: 'Die Voraussetzungen für 0 % MwSt. Lieferung',
        });

        isChecked && isScrollRead && setIsErrorShow(false);

        onClose && onClose();
        handleClose();
        setTimeout(() => {
            callback && callback();
        }, 500);
    };

    // 使用 Intersection Observer 检测读完状态
    useEffect(() => {
        if (open && readIndicatorRef.current) {
            const observer = new IntersectionObserver(
                (entries) => {
                    entries.forEach((entry) => {
                        if (entry.isIntersecting && !isScrollRead) {
                            setIsScrollRead(true);
                        }
                    });
                },
                {
                    threshold: 0.1, // 当10%的元素可见时触发
                    rootMargin: '0px 0px -50px 0px', // 底部留50px边距
                },
            );

            observer.observe(readIndicatorRef.current);

            return () => {
                observer.disconnect();
            };
        }
    }, [open, isScrollRead]);

    // 移除原有的容器高度计算逻辑
    // useEffect(() => {
    //     if (open) {
    //         const element = container?.current?.querySelector('.content');
    //         if (element) {
    //             setContainerHeight(element.clientHeight);
    //         }
    //     }
    // }, [open]);

    if (!open) return <></>;

    return (
        <div className={styled.diaolog}>
            <div className="diaolog-content">
                <div className="my-dialog-content">
                    <button
                        className="my-dialog-close"
                        onClick={() => {
                            handleClose();
                            onClose && onClose();
                        }}
                    ></button>
                    <div>
                        <div className="my-dialog-header my-dialog-agreement-title">{'Die Voraussetzungen für 0 % MwSt. Lieferung'}</div>
                        <div className="my-dialog-body my-agreement-container">
                            <div ref={container} className="my-agreement">
                                <div className="aggreementRead-scroll-container">
                                    <div className="content">
                                        <p>Sehr geehrter Kunde,</p>
                                        <p>
                                            es ist möglich, dass für die ausgewählten Produkte Nullsteuersatz gemäß § 12 Abs. 3 dUStG für Deutschland oder § 28
                                            Abs. 62 öUStG für Österreich gilt. Bitte prüfen Sie, ob die Voraussetzungen in Ihrem Fall erfüllt sind. Der
                                            Verkäufer leistet keine Steuerberatung und übernimmt keine Gewähr für das Eintreten steuerrechtlicher Tatbestände.
                                            Klicken Sie{' '}
                                            <a href="https://www.gesetze-im-internet.de/ustg_1980/__12.html" target="_blank" rel="noreferrer">
                                                hier
                                            </a>{' '}
                                            um den Gesetzestext des § 12 Abs. 3 UStG in Deutschland und{' '}
                                            <a
                                                href="https://www.oesterreich.gv.at/themen/umwelt_und_klima/energie_und_ressourcen_sparen/1/Seite.2430320.html"
                                                target="_blank"
                                                rel="noreferrer"
                                            >
                                                hier
                                            </a>{' '}
                                            um nähere Informationen zu den Voraussetzungen des § 28 Abs. 62 UStG in Österreich zu lesen.
                                        </p>
                                        <p>
                                            1. Bitte prüfen Sie für die nach Deutschland verkaufte(n) Ware(n), ob die Voraussetzungen für die
                                            Umsatzsteuerbefreiung nach § 12 Abs. 3 UStG erfüllt sind. Ist dies der Fall, kreuzen Sie bitte an, dass Sie beim
                                            Kauf der Ware von der Umsatzsteuer befreit sind. Mit dem Ankreuzen dieses Feldes versichern Sie, dass
                                        </p>
                                        <p>- die Liefer- und Rechnungsadresse in Deutschland liegt,</p>
                                        <p>
                                            - das erworbene Produkt auf oder in der Nähe von Privatwohnungen, Wohnungen, öffentlichen und anderen Gebäuden, die
                                            für Tätigkeiten im öffentlichen Interesse genutzt werden, oder auf Wohnwagen oder Wohnschiffen, die nicht oder nur
                                            gelegentlich bewegt werden, installiert wird, und
                                        </p>
                                        <p>
                                            - die installierte Bruttoleistung der Photovoltaikanlage laut Marktstammdatenregister nicht mehr als 30 Kilowatt
                                            (peak) nicht übersteigt oder nicht übersteigen wird.
                                        </p>
                                        <p>- die Liefer- und Rechnungsadresse in Österreich liegt,</p>
                                        <p>
                                            - das erworbene Produkt auf oder an Gebäuden installiert wird, die Wohnzwecken dienen, von Körperschaften des
                                            öffentlichen Rechts oder von privaten Körperschaften, Personenvereinigungen und Vermögensmassen, die gemeinnützigen,
                                            mildtätigen oder kirchlichen Zwecken dienen, genutzt werden, und
                                        </p>
                                        <p>
                                            - die installierte Bruttoleistung der Photovoltaikanlage 35 Kilowatt (Peak) nicht übersteigt oder nicht übersteigen
                                            wird.
                                        </p>
                                        <p>
                                            Die Anwendung des Nullsteuersatzes ist nicht mit einem verbindlichen Preisangebot des Verkäufers verbunden. Der
                                            Gesamtpreis ergibt sich aus den Produktpreisen und Steuern sowie ggf. zusätzlichen Kosten wie Versand etc. Wenn der
                                            Kunde angibt, dass ein Steuersatz von 0% anwendbar ist, wird der angegebene Preis auf dieser Grundlage angezeigt.
                                        </p>
                                        <div
                                            ref={readIndicatorRef}
                                            style={{
                                                height: '1px',
                                                width: '100%',
                                                visibility: 'hidden',
                                            }}
                                            aria-hidden="true"
                                        ></div>
                                    </div>
                                </div>
                            </div>
                            <div className="my-agreement-divider"></div>
                            <div className="checkbox-wrapper">
                                <Checkbox checked={isChecked} onChange={onCheckboxChange}>
                                    Hiermit bestätigen Sie, das Sie die Voraussetzungen für eine umsatzsteuerfreie Lieferung gemäß § 12 Abs. 3 von Deutschland
                                    erfüllen.
                                </Checkbox>
                                {isErrorShow && <ShowErrorTip text="* Bestätigen Sie bitte die Information oben" />}
                            </div>
                            <div className="my-form-btn-box my-agreement-btn-box">
                                <button className={`my-btn-next ${!isScrollRead ? 'my-btn-next-disabled' : ''}`} onClick={handleEnsure}>
                                    Bestätigen
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
